/**
 * HomePageDesigner — operator screen for editing what the public home page
 * shows. First section: the hero carousel (upload / reorder / remove),
 * backed by the bucket via /content-images. Hero copy lives in Site Content
 * and the upcoming schedule has its own screen; links provided below.
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, Button, Flex, Heading, Text } from '@radix-ui/themes';
import api from '../../utils/api';
import './HomePageDesigner.css';

interface CarouselImage {
  url: string;
  alt: string;
  key: string;
}

const HomePageDesigner: React.FC = () => {
  const [images, setImages] = useState<CarouselImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    api
      .get('/content-images?category=carousel')
      .then((r) => setImages(r.data?.data?.images ?? []))
      .catch(() => setError('Could not load carousel images.'))
      .finally(() => setLoading(false));
  }, []);

  const onUpload = async (file: File) => {
    setBusy(true);
    setError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await api.post('/content-images/upload?category=carousel', form);
      const img = res.data?.data?.image as CarouselImage;
      if (img) setImages((prev) => [...prev, img]);
    } catch {
      setError('Upload failed.');
    } finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const onDelete = async (img: CarouselImage) => {
    if (!window.confirm('Remove this image from the carousel?')) return;
    const prev = images;
    setImages((xs) => xs.filter((x) => x.key !== img.key)); // optimistic
    try {
      await api.delete(`/content-images?key=${encodeURIComponent(img.key)}`);
    } catch {
      setError('Delete failed.');
      setImages(prev);
    }
  };

  // Reorder by swapping with a neighbour, then persist the new key order.
  // The server renames files to NN- prefixes and returns the canonical list.
  // ponytail: arrow reorder over drag-and-drop — a carousel has a handful of
  // images; add dnd-kit here only if that stops being true.
  const move = async (i: number, delta: number) => {
    const j = i + delta;
    if (j < 0 || j >= images.length || busy) return;
    const next = [...images];
    [next[i], next[j]] = [next[j], next[i]] as [CarouselImage, CarouselImage];
    setImages(next); // optimistic
    setBusy(true);
    try {
      const res = await api.put('/content-images/reorder', {
        category: 'carousel',
        keys: next.map((x) => x.key),
      });
      setImages(res.data?.data?.images ?? next);
    } catch {
      setError('Reorder failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <Heading size="6" mb="1">Home Page</Heading>
      <Text size="2" color="gray">Edit what visitors see on the public home page.</Text>

      <Box mt="5" className="hp-section">
        <Flex justify="between" align="center" mb="3">
          <Heading size="4">Hero Carousel</Heading>
          <Button disabled={busy} onClick={() => fileInput.current?.click()}>
            {busy ? 'Working…' : 'Upload image'}
          </Button>
          <input
            ref={fileInput}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onUpload(f);
            }}
          />
        </Flex>

        {error && <Text size="2" color="red" as="p" mb="2">{error}</Text>}

        {loading ? (
          <Text size="2" color="gray">Loading…</Text>
        ) : images.length === 0 ? (
          <Text size="2" color="gray">No carousel images yet. Upload one to get started.</Text>
        ) : (
          <div className="hp-carousel-grid">
            {images.map((img, i) => (
              <div className="hp-thumb" key={img.key}>
                <img src={img.url} alt={img.alt} loading="lazy" />
                <div className="hp-thumb-bar">
                  <button
                    type="button"
                    title="Move left"
                    disabled={i === 0 || busy}
                    onClick={() => move(i, -1)}
                  >
                    ‹
                  </button>
                  <span className="hp-thumb-pos">{i + 1}</span>
                  <button
                    type="button"
                    title="Move right"
                    disabled={i === images.length - 1 || busy}
                    onClick={() => move(i, 1)}
                  >
                    ›
                  </button>
                  <button
                    type="button"
                    className="hp-thumb-del"
                    title="Remove"
                    onClick={() => onDelete(img)}
                  >
                    ×
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        <Text size="1" color="gray" as="p" mt="2">
          Order here is the order shown on the home page. Changes go live within a few minutes.
        </Text>
      </Box>

      <Box mt="5" className="hp-section">
        <Heading size="4" mb="2">Hero text &amp; schedule</Heading>
        <Text size="2" color="gray" as="p">
          Headline and subtext live in <Link to="/site-content">Site Content</Link>. Upcoming events
          are managed in <Link to="/schedule">Schedule</Link>.
        </Text>
      </Box>
    </Box>
  );
};

export default HomePageDesigner;
